import React, { useEffect, useRef, useState } from 'react';

// Iter 822 (Kamel) — Tuto « Inscription et règlement » en 3 étapes. Le point qui bloque : ajouter une personne de SON foyer
// (🟨 case jaune, sur sa facture) vs. une personne d'un AUTRE formulaire (🟪 case violette, même chambre, pas la même facture).
export const TUTO_URL = '/tuto-inscription';

const Etape = ({ n, titre, children, id }) => (
  <div id={id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid={`tuto-etape-${n}`}>
    <p className="text-sm font-bold text-[#1E3A5F] mb-2">{n} {titre}</p>
    <div className="text-sm text-gray-700 leading-relaxed space-y-1.5 text-left font-normal">{children}</div>
  </div>
);

export const TutoInscriptionContenu = ({ compact = false }) => (
  <div className="space-y-3">
    {!compact && (
      <Etape n="1️⃣" titre="Remplissez votre formulaire" id="tuto-etape-1">
        <p><strong>Un formulaire par voyage et par foyer</strong> habitant à la même adresse, même si vous ne portez pas le même nom.</p>
        <p>Le formulaire de réservation bloque vos places : <a href="/inscription" className="text-[#1E3A5F] underline font-semibold">rhoneamitievoyages.fr/inscription</a></p>
      </Etape>
    )}
    <Etape n="2️⃣" titre="Vous voyagez avec quelqu'un qui n'habite pas à la même adresse que vous ?" id="tuto-etape-2">
      <p><strong>Chacun doit remplir son propre formulaire.</strong> Au moment d'ajouter une personne dans une chambre, le formulaire vous demande : <em>« Cette personne habite-t-elle à la même adresse que vous ? »</em> — l'adresse du foyer compte, pas le nom de famille.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
        <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3" data-testid="tuto-case-jaune">
          <p className="font-bold text-amber-900 text-sm">🟨 OUI — Même adresse que moi</p>
          <p className="text-xs text-amber-900 mt-1">Elle sera sur <strong>mon formulaire et ma facture</strong>. L'association vérifie chaque formulaire avant de le confirmer.</p>
          <p className="text-xs text-amber-800 italic mt-1.5">Ex. : maman et enfant, ou couple, même avec des noms différents.</p>
        </div>
        <div className="rounded-lg border-2 border-violet-400 bg-violet-50 p-3" data-testid="tuto-case-violette">
          <p className="font-bold text-violet-900 text-sm">🟪 NON — Autre adresse</p>
          <p className="text-xs text-violet-900 mt-1"><strong>Nom et prénom uniquement</strong> pour la chambre. Pas d'inscription ni de facture sur ce formulaire.</p>
          <p className="text-xs text-violet-900 mt-1">Elle devra remplir son propre formulaire (et y indiquer votre nom de la même façon).</p>
          <p className="text-xs text-violet-800 italic mt-1.5">Ex. : deux sœurs à des adresses différentes.</p>
          <p className="text-xs font-bold text-violet-900 mt-1.5">👉 Vous partagez la chambre, pas la facture.</p>
        </div>
      </div>
    </Etape>
    {!compact && (
      <Etape n="3️⃣" titre="Réglez depuis votre espace adhérent" id="tuto-etape-3">
        <p>Après l'envoi du formulaire, <strong>votre espace adhérent s'ouvre immédiatement</strong>. Pour payer le voyage ou l'acompte :</p>
        <ul className="list-disc ml-5 space-y-0.5">
          <li>💳 <strong>Carte bancaire ou virement</strong> : ouvrez « Payer à distance (CB, virement, chèques-vacances) ».</li>
          <li>📅 <strong>Espèces ou chèques-vacances papier</strong> : ouvrez « Mon rendez-vous à la permanence » et choisissez un créneau.</li>
        </ul>
        <p>Vous retrouverez votre facture et vos paiements dans « Ma facture &amp; mes règlements ».</p>
      </Etape>
    )}
  </div>
);

export const TutoInscriptionModal = ({ open, onClose, etape }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { const el = ref.current?.querySelector(`#tuto-etape-${etape || 1}`); el?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, 80);
    return () => clearTimeout(t);
  }, [open, etape]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="tuto-inscription-modal">
      <div className="bg-gray-50 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 bg-[#1E3A5F] text-white rounded-t-2xl">
          <p className="text-sm font-bold">📖 Inscription et règlement du voyage — en 3 étapes</p>
          <button type="button" onClick={onClose} className="text-sm px-2 py-1 rounded hover:bg-white/10" data-testid="tuto-inscription-fermer">✕</button>
        </div>
        <div ref={ref} className="p-4 overflow-y-auto text-left font-normal normal-case"><TutoInscriptionContenu /></div>
        <div className="px-4 py-3 border-t bg-white rounded-b-2xl flex items-center justify-between gap-2 flex-wrap">
          <a href={TUTO_URL} target="_blank" rel="noreferrer" className="text-xs text-[#1E3A5F] underline">Ouvrir ce tuto dans une page (à partager) ↗</a>
          <button type="button" onClick={onClose} className="text-sm font-bold bg-[#1E3A5F] text-white rounded-lg px-4 py-2" data-testid="tuto-inscription-ok">J'ai compris, je continue</button>
        </div>
      </div>
    </div>
  );
};

// Petit lien discret « ❓ Lequel choisir ? » qui ouvre le tuto directement sur l'étape 2 (le point qui bloque)
export const TutoLienChoix = ({ etape = 2, label = '❓ Lequel choisir ?', className = '' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`text-[11px] font-bold text-[#1E3A5F] underline decoration-dotted hover:text-sky-700 ${className}`} data-testid={`tuto-lien-${etape}`}>{label}</button>
      <TutoInscriptionModal open={open} onClose={() => setOpen(false)} etape={etape} />
    </>
  );
};
